import {
  createElement,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from "react";

import ClearIcon from "@mui/icons-material/Clear";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import {
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useDataGridStore } from "../../store/DataGrid";
import {
  dropdownMenuListProps,
  dropdownMenuPaperProps,
  LIST_TOOLBAR_HALF_GAP,
  navDropdownMenuItemSx,
} from "../dropdownMenuStyles";

interface SearchFieldProps {
  /** @figma Pattern 158: labelled field selector and visible clear control.
   * https://www.figma.com/design/HDBjSeAYxCpDOHrBaib9ik/Tools?node-id=3-1366 */
  presentation?: "moduleList";
  clearSearch: () => void;
  onChange: (e,searchFieldText:string,filterObj:{ field: string; operator: string; value: string; }) => void;
  value: string;
  items?: { [key: string]: unknown }[];
}

export function SearchField(props: SearchFieldProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [menuItems, setMenuItems] = useState<ReactElement[]>([]);
  const { 
    setQuickSearchField:setSearchField,
    quickSearchField:searchField,
    operator,
    setOperator:setOperatorValue,
    setQuickSearchValue
   } = useDataGridStore();
  const onClear = useCallback(() => {
    setSearchField("q");
    props.clearSearch();
  }, [props,setSearchField]);

  const handleItemClick = useCallback(
    (value: string, op: string) => {
      setAnchorEl(null);
      setSearchField(value);
      setOperatorValue(op);
      props.clearSearch();
    },
    [props,setSearchField,setOperatorValue]
  );

  useEffect(() => {
    if (props.items) {
      const menus = props.items.map((item, index) => {
        const selected = searchField === item.field;
        const menuItem = createElement(
          MenuItem,
          {
            "aria-selected": selected ? "true" : undefined,
            selected,
            onClick: () =>
              handleItemClick(
                item.field as string,
                item.operator as string
              ),
            // @ts-ignore
            "data-value": item.field,
            "data-value-readable": item.label,
            key: index,
            sx: navDropdownMenuItemSx,
          },
          item.label as string
        );
        return menuItem;
      });
      setMenuItems(menus);
    }
  }, [props.items, searchField, handleItemClick]);
  const onChange = (e) => {
    const filterObj =  {
           field: searchField,
           operator: operator,
           value: e.target.value,
         }
    setQuickSearchValue(e.target.value)
    //  apiRef.current.upsertFilterItem(filterObj)
     props.onChange(e,searchField,filterObj);
  };

  const displayName = (value: any) =>
    menuItems.find((item) => item.props["data-value"] === value)?.props["data-value-readable"] ?? value;

  return (
    <TextField
      value={props?.value}
      onChange={onChange}
      placeholder="Find…"
      size="small"
      margin="none"
      sx={{
        ...(menuItems.length > 0 ? { minWidth: 330 } : {}),
        mx: props.presentation === "moduleList" ? 0 : { xs: 1, sm: LIST_TOOLBAR_HALF_GAP },
        width: "auto",
        "& .MuiOutlinedInput-root": {
          height: "36px",
          minHeight: "36px",
          maxHeight: "36px",
          boxSizing: "border-box",
          borderRadius: "4px",
          fontSize: "0.875rem",
          paddingLeft: "4px",
          paddingRight: "4px",
        },
        "& .MuiOutlinedInput-input": {
          padding: "0 8px",
          height: "36px",
          boxSizing: "border-box",
          letterSpacing: "0.15px",
        },
        "& .MuiOutlinedInput-input::placeholder": {
          color: grey[500],
          opacity: 1,
        },
        "& .MuiInputBase-adornedStart": {
          paddingLeft: "4px",
        },
      }}
      InputProps={{
        startAdornment: (
          <>
            <IconButton
              id="search-button"
              aria-label="Search field"
              aria-controls={open ? "search-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              color="secondary"
              size="small"
              // @figma Component 33 · Search Field: magnifier, field name and
              // caret side by side, 4px apart, grey[500] glyphs.
              sx={{ p: "4px", mr: 0, gap: "4px", color: grey[500] }}
              onClick={(event) => setAnchorEl(event.currentTarget)}
            >
              <SearchIcon sx={{ m: 0 }} fontSize="small" />
              {searchField !== "q" || props.presentation === "moduleList" ? (
                <Typography
                  sx={{ fontSize: "14px", lineHeight: "20px", letterSpacing: "0.15px", color: grey[700] }}
                >
                  {searchField === "q" ? "All fields" : menuItems.length > 0 && displayName(searchField)}
                </Typography>
              ) : null}
              {menuItems.length > 0 ? (
                <ArrowDropDownIcon fontSize="small" />
              ) : null}
            </IconButton>
            <Menu
              id="search-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              PaperProps={dropdownMenuPaperProps}
              MenuListProps={{
                ...dropdownMenuListProps,
                "aria-labelledby": "search-button",
              }}
            >
              {menuItems}
            </Menu>
          </>
        ),
        endAdornment: (
          <IconButton
            title="Clear"
            aria-label="Clear"
            size="small"
            sx={{
              p: "4px",
              visibility:
                props.presentation === "moduleList" || props?.value || searchField !== "q" ? "visible" : "hidden",
            }}
            onClick={onClear}
          >
            <ClearIcon sx={{ fontSize: "18px", color: grey[500] }} />
          </IconButton>
        ),
      }}
    />
  );
}
